import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

let isRefreshing = false;
let failedQueue = [];
let interceptorsRegistered = false;

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

const setupInterceptors = (setIsAuth) => {
	if (interceptorsRegistered) return;
	interceptorsRegistered = true;

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token) {
				try {
					config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
					setIsAuth(true);
				} catch (e) {
					
				}
			}
			return config;
		},
		error => Promise.reject(error)
	);

	api.interceptors.response.use(
		response => response,
		async error => {
			const originalRequest = error.config;

			if (
				error?.response?.status === 403 &&
				originalRequest &&
				!originalRequest._retry &&
				!originalRequest.url?.includes("/auth/refresh_token") &&
				!originalRequest.url?.includes("/auth/login")
			) {
				if (isRefreshing) {
					return new Promise((resolve, reject) => {
						failedQueue.push({ resolve, reject });
					})
						.then(token => {
							originalRequest.headers["Authorization"] = `Bearer ${token}`;
							return api(originalRequest);
						})
						.catch(err => Promise.reject(err));
				}

				originalRequest._retry = true;
				isRefreshing = true;

				try {
					const { data } = await api.post("/auth/refresh_token");
					if (data?.token) {
						localStorage.setItem("token", JSON.stringify(data.token));
						api.defaults.headers.Authorization = `Bearer ${data.token}`;
						originalRequest.headers["Authorization"] = `Bearer ${data.token}`;
						processQueue(null, data.token);
						return api(originalRequest);
					}
				} catch (refreshError) {
					processQueue(refreshError, null);
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
					return Promise.reject(refreshError);
				} finally {
					isRefreshing = false;
				}
			}

			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}

			return Promise.reject(error);
		}
	);
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		setupInterceptors(setIsAuth);
	}, []);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					if (data?.token) {
						localStorage.setItem("token", JSON.stringify(data.token));
						api.defaults.headers.Authorization = `Bearer ${data.token}`;
						setIsAuth(true);
						setUser(data.user || {});
					}
				} catch (err) {
					localStorage.removeItem("token");
					setIsAuth(false);
				}
			}
			setLoading(false);
		})();
	}, []);


	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/atendimentos");
			setLoading(false);
		} catch (err) {
			setLoading(false);
			const status = err?.response?.status;
			const msg = !err?.response
				? "Não foi possível conectar ao servidor. Verifique sua internet e tente de novo."
				: status === 401 || status === 403
				? "E-mail ou senha incorretos. Confira e tente de novo."
				: "Não foi possível entrar agora. Tente de novo em instantes.";
			toast.error(msg, { toastId: "login-erro" });
			return { erro: msg };
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		}
	};

	useEffect(() => {
		if (!isAuth) return;

		const socket = openSocket();

		const onUser = data => {
			if (data.action === "update") {
				setUser(prevUser => {
					if (data.user.id === prevUser.id) {
						return { ...prevUser, ...data.user };
					}
					return prevUser;
				});
			}
		};
		socket.on("user", onUser);

		const onForceLogout = () => {
			toast.error("Você foi desconectado pelo administrador.");
			handleLogout();
		};
		socket.on("force_logout", onForceLogout);

		return () => {
			socket.off("user", onUser);
			socket.off("force_logout", onForceLogout);
		};
		
	}, [isAuth]);

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
